import styled from '@emotion/styled';
import { breakpoints, colors, spacing } from '../../../../utils/styleguide';
import Container from '../Container';
import SectionTitle from '../../SectionTitle';
import InitiativeCard from './InitiativeCard';
import { Asset } from 'contentful';
import Typography from '../../text/Typography';
import PrimaryButton from '../../buttons/PrimaryButton';

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]}px;
`;

const Grid = styled.div`
  margin: 0 auto;
  display: grid;
  grid-row-gap: ${spacing[4]}px;
  grid-column-gap: ${spacing[4]}px;
  grid-template-columns: repeat(auto-fit, minmax(327px, 1fr));

  ${breakpoints.desktop} {
    padding: 0 ${spacing[5]}px;
    grid-template-columns: repeat(auto-fit, minmax(348px, 1fr));
  }

  // Custom brekapoint to force 2 columns for up to 1440px without causing it to jump to 1 column before tablet breakpoint
  @media (min-width: 1390px) {
    grid-template-columns: repeat(auto-fit, minmax(370px, 1fr));
  }
`;

type Props = {
  sectionTitle?: string;
  ctaTitle?: string;
  ctaDescription?: string;
  ctaButtonLabel?: string;
  initiatives: {
    id: string;
    pillarName?: string;
    pillarColor?: string;
    image?: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>;
    iconUrl?: string;
    iconAlt?: string;
    name: string;
    location?: string;
    description?: string;
    slug?: string;
  }[];
  backgroundColor?: string;
  fundSlug?: string;
};

export default function ProjectListingWithPreview({
  sectionTitle,
  ctaTitle,
  ctaDescription,
  ctaButtonLabel,
  initiatives,
  backgroundColor,
  fundSlug,
}: Props) {
  return (
    <StyledContainer backgroundColor={backgroundColor}>
      {sectionTitle && <SectionTitle title={sectionTitle} />}
      <Grid>
        {initiatives.map((initiative) => (
          <InitiativeCard
            key={initiative.name}
            image={initiative.image}
            title={initiative.name}
            location={initiative.location}
            description={initiative.description}
            overlayIconUrl={initiative.iconUrl}
            overlayIconAlt={initiative.iconAlt}
            cardLinkUrl={
              initiative.slug ? `/impact-funds/${fundSlug}/projects/${initiative.slug}` : undefined
            }
            border
            filter={
              initiative.pillarName && initiative.pillarColor
                ? {
                    filterName: initiative.pillarName,
                    backgroundColor: initiative.pillarColor,
                  }
                : undefined
            }
          />
        ))}
        {fundSlug && ctaTitle && ctaDescription && ctaButtonLabel && (
          <SeeAllCard
            title={ctaTitle}
            description={ctaDescription}
            ctaButtonLabel={ctaButtonLabel}
            href={`/impact-funds/${fundSlug}/projects`}
          />
        )}
      </Grid>
    </StyledContainer>
  );
}

const SeeAllWrapper = styled.div`
  padding: ${spacing[2]}px ${spacing[5]}px;
  border-left: 2px solid ${colors.purple200};
  margin-top: ${spacing[4]}px;
`;

const SeeAllTitle = styled(Typography)`
  margin-bottom: ${spacing[1]}px;
`;

const SeeAllButton = styled(PrimaryButton)`
  margin-top: ${spacing[4]}px;
`;

function SeeAllCard({
  title,
  description,
  ctaButtonLabel,
  href,
}: {
  title: string;
  description: string;
  ctaButtonLabel: string;
  href: string;
}) {
  return (
    <div>
      <SeeAllWrapper>
        <SeeAllTitle variant="h4">{title}</SeeAllTitle>
        <Typography variant="body">{description}</Typography>
        <SeeAllButton label={ctaButtonLabel} href={href} />
      </SeeAllWrapper>
    </div>
  );
}
